import { useNavigate } from 'react-router-dom';
import { MessageSquare, Users, CreditCard, Zap, Shield, Clock, TrendingUp, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-16 pb-20 lg:pt-24 lg:pb-32">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-600/5 to-transparent" />
        
        <div className="relative container mx-auto px-6">
          <div className="flex flex-col items-center text-center">
            {/* Logo */}
            <div className="mb-8 px-8 py-6 bg-gradient-to-br from-emerald-500 to-green-600 rounded-3xl shadow-2xl shadow-emerald-500/30">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-12 h-12 md:w-14 md:h-14 text-white" strokeWidth={2.5} />
                <span className="text-4xl md:text-5xl font-black tracking-tight text-white" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                  MeuGestor
                </span>
              </div>
            </div>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl">
              Automatize cobranças via WhatsApp. Gerencie clientes ilimitados com notificações automáticas e controle total.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Button 
                onClick={() => navigate('/register')} 
                className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-semibold shadow-lg shadow-emerald-500/20 transition-all hover:-translate-y-0.5 h-14 px-10 rounded-xl text-lg"
                data-testid="get-started-btn"
              >
                Começar Teste Grátis
              </Button>
              <Button 
                onClick={() => navigate('/login')} 
                className="bg-transparent border-2 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-400 font-semibold transition-colors h-14 px-10 rounded-xl text-lg"
                data-testid="login-btn"
              >
                Já tenho conta
              </Button>
            </div>
            
            <p className="text-sm text-slate-400">
              ✨ 7 dias grátis • Sem cartão de crédito • Depois apenas R$19,90/mês
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-zinc-900/30">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">Recursos Completos</h2>
          <p className="text-slate-400 text-center mb-12 max-w-xl mx-auto">Tudo que você precisa para gerenciar seus clientes e automatizar cobranças</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: MessageSquare,
                title: 'WhatsApp Integrado',
                description: 'Envie mensagens automáticas via WhatsApp',
                color: 'text-emerald-400'
              },
              {
                icon: Monitor,
                title: 'Múltiplas Telas',
                description: 'Gerencie várias telas por cliente',
                color: 'text-sky-400'
              },
              {
                icon: Users,
                title: 'Gestão de Clientes',
                description: 'CRUD completo com grupos e tags',
                color: 'text-violet-400'
              },
              {
                icon: CreditCard,
                title: 'Cobranças Automáticas',
                description: 'Lembretes de vencimento automáticos',
                color: 'text-rose-400'
              },
              {
                icon: Zap,
                title: 'Automação Total',
                description: 'Configure templates e agendamentos',
                color: 'text-amber-400'
              },
              {
                icon: Shield,
                title: 'Seguro e Confiável',
                description: 'Seus dados sempre protegidos',
                color: 'text-green-400'
              },
              {
                icon: Clock,
                title: 'Teste de Clientes',
                description: 'Libere acessos temporários controlados',
                color: 'text-blue-400'
              },
              {
                icon: TrendingUp,
                title: 'Escale seu Negócio',
                description: 'Cresça sem limites de clientes',
                color: 'text-purple-400'
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="group relative overflow-hidden bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 hover:border-emerald-500/30 transition-all hover:-translate-y-1">
                  <Icon className={`w-10 h-10 ${feature.color} mb-4`} />
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-20 bg-zinc-950">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">Plano Simples</h2>
          <p className="text-slate-400 text-center mb-12">Sem surpresas, sem taxas escondidas</p>
          
          <div className="max-w-md mx-auto">
            <div className="relative bg-gradient-to-b from-zinc-800 to-zinc-900 border border-zinc-700 rounded-3xl p-8 shadow-2xl">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">MAIS POPULAR</span>
              </div>
              
              <div className="text-center mt-4 mb-6">
                <h3 className="text-2xl font-bold mb-2">Plano Premium</h3>
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">R$19,90</span>
                  <span className="text-slate-400 ml-2">/mês</span>
                </div>
              </div>
              
              <ul className="space-y-4 mb-8">
                {[
                  '7 dias de teste grátis',
                  'Clientes ilimitados',
                  'Múltiplas telas por cliente',
                  'WhatsApp integrado',
                  'Cobranças automáticas',
                  'Suporte prioritário'
                ].map((item, index) => (
                  <li key={index} className="flex items-center">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center mr-3">
                      <svg className="w-3 h-3 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => navigate('/register')} 
                className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-semibold shadow-lg shadow-emerald-500/20 transition-all hover:-translate-y-0.5 h-14 rounded-xl text-lg"
              >
                Iniciar Teste Grátis
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-emerald-600 via-green-600 to-emerald-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Comece Hoje!</h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-xl mx-auto">Automatize suas cobranças e ganhe mais tempo para o que importa</p>
          <Button 
            onClick={() => navigate('/register')} 
            className="bg-white text-emerald-600 hover:bg-slate-50 font-bold shadow-xl transition-all hover:-translate-y-1 h-14 px-10 rounded-xl text-lg"
          >
            Começar Agora →
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800 py-8">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4 opacity-70">
            <MessageSquare className="w-8 h-8 text-emerald-500" strokeWidth={2.5} />
            <span className="text-xl font-bold text-white" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>MeuGestor</span>
          </div>
          <p className="text-slate-500 text-sm">© 2026 MeuGestor. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
